import { ReactNode, useRef, useState } from "react";
import { Plus, File as FileIcon, Link as LinkIcon, X, Globe, FileUp } from "lucide-react";
import { FigmaTypography } from "@libs/zapi-ui/custom";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "@libs/zapi-ui/shadcn";
import { cn } from "@/zapi-frontend/shared/lib/utils";
import { AddWebLinkModal } from "./AddWebLinkModal";

type TStagedAttachments = {
  hasAttachments: boolean;
  chips: ReactNode;
  attachButton: ReactNode;
  overlays: ReactNode;
  take: () => { files: File[]; links: string[] };
};

export function useStagedAttachments({ disabled = false }: { disabled?: boolean } = {}): TStagedAttachments {
  const [stagedFiles, setStagedFiles] = useState<File[]>([]);
  const [stagedLinks, setStagedLinks] = useState<string[]>([]);
  const [isAttachMenuOpen, setIsAttachMenuOpen] = useState(false);
  const [isLinkModalOpen, setIsLinkModalOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const hasAttachments = stagedFiles.length > 0 || stagedLinks.length > 0;

  const removeAt = (kind: "file" | "link", index: number) => {
    if (kind === "file") setStagedFiles((prev) => prev.filter((_, i) => i !== index));
    else setStagedLinks((prev) => prev.filter((_, i) => i !== index));
  };

  const chip = (kind: "file" | "link", label: string, index: number) => (
    <div key={`${kind}-${index}`} className="flex gap-2 items-center w-full">
      <div className="flex flex-1 gap-2 items-center min-w-0">
        {kind === "file" ? (
          <FileIcon className="size-4 shrink-0 text-innoveraZinc-900" />
        ) : (
          <LinkIcon className="size-4 shrink-0 text-innoveraZinc-900" />
        )}
        <FigmaTypography variant="body-2" color="zinc900" className="truncate">
          {label}
        </FigmaTypography>
      </div>
      <button
        type="button"
        onClick={() => removeAt(kind, index)}
        aria-label="Remove"
        className="size-4 shrink-0 flex items-center justify-center text-innoveraZinc-500 hover:text-innoveraZinc-900"
      >
        <X className="size-4" />
      </button>
    </div>
  );

  const chips = hasAttachments ? (
    <div className="flex flex-col gap-1.5 px-3 pt-1">
      {stagedFiles.map((file, idx) => chip("file", file.name, idx))}
      {stagedLinks.map((link, idx) => chip("link", link, idx))}
    </div>
  ) : null;

  const attachButton = (
    <DropdownMenu open={isAttachMenuOpen} onOpenChange={setIsAttachMenuOpen}>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          aria-label="Attach"
          className={cn(
            "h-6 flex items-center justify-center px-2 rounded-md bg-innoveraZinc-100 text-innoveraZinc-900",
            "hover:bg-innoveraZinc-150 disabled:opacity-50 disabled:pointer-events-none"
          )}
        >
          <Plus className="size-3" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-[102px] p-1" side="top" sideOffset={4}>
        <DropdownMenuItem
          onSelect={() => {
            setIsAttachMenuOpen(false);
            fileInputRef.current?.click();
          }}
          className="gap-1 px-2 py-1.5 text-[12px]"
        >
          <FileUp className="size-3" />
          Files
        </DropdownMenuItem>
        <DropdownMenuItem
          onSelect={() => {
            setIsAttachMenuOpen(false);
            setIsLinkModalOpen(true);
          }}
          className="gap-1 px-2 py-1.5 text-[12px]"
        >
          <Globe className="size-3" />
          Web Link
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );

  const overlays = (
    <>
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept=".pdf,.pptx,.jpg,.jpeg,.png,.txt,.csv,.xlsx,.xls,.xlsm,.docx"
        onChange={(e) => {
          if (!e.target.files || e.target.files.length === 0) return;
          const picked = Array.from(e.target.files);
          setStagedFiles((prev) => [...prev, ...picked]);
          e.target.value = "";
        }}
        style={{ display: "none" }}
      />
      <AddWebLinkModal
        isOpen={isLinkModalOpen}
        onClose={() => setIsLinkModalOpen(false)}
        onSubmit={(url) => setStagedLinks((prev) => [...prev, url])}
      />
    </>
  );

  const take = () => {
    const result = { files: stagedFiles.slice(), links: stagedLinks.slice() };
    setStagedFiles([]);
    setStagedLinks([]);
    return result;
  };

  return { hasAttachments, chips, attachButton, overlays, take };
}
